import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getPairedRaw, subscribeTerminalDevice } from "@/lib/terminal/terminalDevice";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ErrorTrackerProvider, useErrorTrackerContextSafe } from "@/contexts/ErrorTrackerContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { StatusBarSync } from "@/components/StatusBarSync";
import { NavigationTracker } from "@/components/NavigationTracker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { I18nProvider } from "@/components/I18nProvider";
import { ShopCartProvider } from "@/contexts/ShopCartContext";
import { PageTitleProvider } from "@/contexts/PageTitleContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { setupGlobalErrorHandlers } from "@/lib/errorLogger";
import { isDespiaNative } from "@/lib/isDespiaNative";
import { MetaPixel } from "@/components/tracking/MetaPixel";
import { DespiaOneSignalSync } from "@/components/DespiaOneSignalSync";
import { DespiaNotificationRouter } from "@/components/DespiaNotificationRouter";
import { InvoiceFileOutboxSync } from "@/components/inventory/InvoiceFileOutboxSync";
import { GoogleAnalytics } from "@/components/tracking/GoogleAnalytics";
import { Suspense, lazy, useEffect, useSyncExternalStore } from "react";
import { useKeyboardOpenClass } from "@/hooks/useKeyboardOpenClass";
import "@/i18n/config"; // Ensure i18n is initialized
import Index from "./pages/Index";
import { SplashGate } from "@/components/ui/SplashGate";
import { PublicAppBannerHost } from "@/components/landing/PublicAppBannerHost";
import { SupportSessionBanner } from "@/components/SupportSessionBanner";

// Lazy-loaded routes to reduce initial JS bundle

const TrialLanding = lazy(() => import("./pages/landing/TrialLanding"));
const DemoLanding = lazy(() => import("./pages/landing/DemoLanding"));
const PestLanding = lazy(() => import("./pages/landing/PestLanding"));

const LandingContact = lazy(() => import("./pages/landing/LandingContact"));
const LandingPrivacy = lazy(() => import("./pages/landing/LandingPrivacy"));
const LandingTerms = lazy(() => import("./pages/landing/LandingTerms"));
const LandingCookies = lazy(() => import("./pages/landing/LandingCookies"));
const LandingDPA = lazy(() => import("./pages/landing/LandingDPA"));
const LandingBooking = lazy(() => import("./pages/landing/LandingBooking"));
const LandingBlog = lazy(() => import("./pages/landing/LandingBlog"));
const LandingBlogPost = lazy(() => import("./pages/landing/LandingBlogPost"));
const Opiniones = lazy(() => import("./pages/landing/Opiniones"));
const Auth = lazy(() => import("./pages/Auth"));
const AppSwitch = lazy(() => import("./pages/AppSwitch"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AppAuth = lazy(() => import("./pages/AppAuth"));
const AppAuthFreeSignup = lazy(() => import("./pages/AppAuthFreeSignup"));
const AppAuthCompleteRegistration = lazy(() => import("./pages/AppAuthCompleteRegistration"));
const CompleteRegistration = lazy(() => import("./pages/CompleteRegistration"));
const PaymentSuccessNative = lazy(() => import("./pages/PaymentSuccessNative"));

const Register = lazy(() => import("./pages/Register"));
const AuthLoading = lazy(() => import("./pages/AuthLoading"));
const ApplePopupCallback = lazy(() => import("./pages/ApplePopupCallback"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const TerminalPage = lazy(() => import("./pages/terminal/TerminalPage"));
const OnboardingGoals = lazy(() => import("./pages/onboarding/OnboardingGoals"));
const OnboardingSetup = lazy(() => import("./pages/onboarding/OnboardingSetup"));
const ShopSupplierUpdate = lazy(() => import("./pages/ShopSupplierUpdate"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const AuthBridge = lazy(() => import("./pages/AuthBridge"));
const AuthErrorPage = lazy(() => import("./pages/AuthErrorPage"));
const SupportAccess = lazy(() => import("./pages/SupportAccess"));
const UnirsePerito = lazy(() => import("./pages/UnirsePerito"));
import { RoleBasedRouter } from "@/components/routing/RoleBasedRouter";
import { ColdStartGate } from "@/components/routing/ColdStartGate";
import { persistidorOffline, vigilarCache } from "@/lib/offline/persistidorOffline";
import { alRecuperarRed, modoActivo, suscribirEstadoOffline } from "@/lib/offline/estadoOffline";
import { SincronizacionOffline } from "@/components/offline/SincronizacionOffline";
import { AvisoSinConexion } from "@/components/offline/AvisoSinConexion";

const OPCIONES_DE_SIEMPRE = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};
const queryClient = new QueryClient({ defaultOptions: { queries: OPCIONES_DE_SIEMPRE } });

/* EL MODO SIN CONEXIÓN EN LA CACHÉ (ver src/lib/offline/), solo con su interruptor
   encendido para la empresa (`platform_settings.modo_sin_conexion`); apagado, las
   opciones de siempre:
   · `persister`: cada consulta se guarda en el teléfono y, al volver a pedirla,
     se pinta AL INSTANTE con la copia y se refresca por detrás;
   · `networkMode: "offlineFirst"`: sin red la consulta se lanza igual (con el
     modo por defecto ni arrancaría y la copia no se leería nunca);
   · `gcTime` 30 min: lo visto se queda más en memoria (la copia sigue en el
     teléfono aunque la memoria la suelte);
   · al volver la red se refresca lo que haya en pantalla. */
let modoAplicado: boolean | null = null;
const aplicarModoSinConexion = () => {
  const activo = modoActivo();
  if (activo === modoAplicado) return;
  modoAplicado = activo;
  queryClient.setDefaultOptions({
    queries: activo
      ? { ...OPCIONES_DE_SIEMPRE, gcTime: 30 * 60 * 1000, networkMode: "offlineFirst", persister: persistidorOffline }
      : OPCIONES_DE_SIEMPRE,
  });
};
aplicarModoSinConexion();
suscribirEstadoOffline(aplicarModoSinConexion);
vigilarCache(queryClient);
alRecuperarRed(() => {
  if (modoActivo()) void queryClient.invalidateQueries();
});

// Component to initialize global error handlers with auth and tracking context
function GlobalErrorHandlerSetup() {
  const { user, company } = useAuth();
  const trackerContext = useErrorTrackerContextSafe();
  
  useEffect(() => {
    setupGlobalErrorHandlers(
      () => ({
        user_id: user?.id,
        company_id: company?.id,
      }),
      () => trackerContext?.getErrorContext() || {
        module: null,
        userAction: null,
        entityType: undefined,
        entityId: undefined,
        entityName: undefined,
        actionTrace: [],
      }
    );
  }, [user?.id, company?.id, trackerContext]);
  
  return null;
}

// Cold-start lazy fallback must stay invisible. The only visible startup
// passage is the WelcomeBackScreen owned by SplashGate.
const LoadingFallback = () => null;

// Guard: blocks all public/landing routes when auth_entry === 'app-auth'
function AppAuthPublicGuard({ children }: { children: React.ReactNode }) {
  if (!isDespiaNative()) return <>{children}</>;
  return <Navigate to="/app-auth" replace />;
}

/**
 * Sin interfaz: marca `body.keyboard-open` mientras el teclado esté abierto para
 * que la barra flotante de abajo no se quede encima de él (ver index.css).
 */
function KeyboardInsetSetup() {
  useKeyboardOpenClass();
  return null;
}

/**
 * Terminal de la finca (30-10-2026, Pablo: «no debe haber forma de salir del modo
 * terminal»): una tablet vinculada vive en /terminal. Cualquier otra ruta la devuelve
 * ahí; solo «Cerrar terminal» (usuario y contraseña de un admin) la desvincula.
 */
function TerminalDeviceLock() {
  const { pathname } = useLocation();
  // Se entera al momento de vincular o cerrar, en esta vista y en las demás.
  const paired = useSyncExternalStore(subscribeTerminalDevice, getPairedRaw, () => null) !== null;
  if (paired && !pathname.startsWith("/terminal")) return <Navigate to="/terminal" replace />;
  return null;
}

const App = () => (
  <ErrorBoundary>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ErrorTrackerProvider>
            <Suspense fallback={<LoadingFallback />}>
              <KeyboardInsetSetup />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <CookieConsentProvider>
                <AuthProvider>
                  <SplashGate>
                  <PageTitleProvider>
                  <ShopCartProvider>
                    <ScrollToTop />
                    <StatusBarSync />
                    <NavigationTracker />
                    <MetaPixel />
                    <GoogleAnalytics />
                    <GlobalErrorHandlerSetup />
                    <DespiaOneSignalSync />
                    {/* Lleva a su sitio el aviso que se toca desde el móvil. */}
                    <DespiaNotificationRouter />
                    {/* Termina de subir las facturas que se quedaron a medias. */}
                    <InvoiceFileOutboxSync />
                    {/* Modo sin conexión: guarda en el móvil todo lo que el usuario
                        puede ver y avisa cuando se está enseñando lo guardado. */}
                    <SincronizacionOffline />
                    <AvisoSinConexion />
                    <PublicAppBannerHost />
                    <SupportSessionBanner />
                    <ColdStartGate>
                    <TerminalDeviceLock />
                    <Routes>
                      {/* Public routes - Landing pages (blocked for app-auth) */}
                      <Route path="/" element={<AppAuthPublicGuard><PestLanding /></AppAuthPublicGuard>} />
                      <Route path="/legacy" element={<AppAuthPublicGuard><Index /></AppAuthPublicGuard>} />
                      
                      <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
                      <Route path="/trial" element={<AppAuthPublicGuard><TrialLanding /></AppAuthPublicGuard>} />

                      <Route path="/demo" element={<AppAuthPublicGuard><DemoLanding /></AppAuthPublicGuard>} />
                      <Route path="/pest" element={<AppAuthPublicGuard><PestLanding /></AppAuthPublicGuard>} />
                      
                      <Route path="/contact" element={<AppAuthPublicGuard><LandingContact /></AppAuthPublicGuard>} />
                      <Route path="/privacy" element={<AppAuthPublicGuard><LandingPrivacy /></AppAuthPublicGuard>} />
                      <Route path="/terms" element={<AppAuthPublicGuard><LandingTerms /></AppAuthPublicGuard>} />
                      <Route path="/cookies" element={<AppAuthPublicGuard><LandingCookies /></AppAuthPublicGuard>} />
                      <Route path="/dpa" element={<AppAuthPublicGuard><LandingDPA /></AppAuthPublicGuard>} />
                      <Route path="/booking" element={<AppAuthPublicGuard><LandingBooking /></AppAuthPublicGuard>} />
                      <Route path="/blog" element={<AppAuthPublicGuard><LandingBlog /></AppAuthPublicGuard>} />
                      <Route path="/blog/:slug" element={<AppAuthPublicGuard><LandingBlogPost /></AppAuthPublicGuard>} />
                      <Route path="/opiniones" element={<AppAuthPublicGuard><Opiniones /></AppAuthPublicGuard>} />
                      
                      {/* Public supplier update page */}
                      <Route path="/shop/supplier-update/:token" element={<ShopSupplierUpdate />} />
                      
                      {/* OAuth callback for identity linking */}
                      <Route path="/oauth/callback" element={<OAuthCallback />} />
                      
                      {/* Native OAuth bridge: receives tokens from /native-callback.html via deep-link */}
                      <Route path="/auth-bridge" element={<AuthBridge />} />

                      {/* Ulture support login-as: redeems a one-time token, then hands off to /auth-loading */}
                      <Route path="/support-access" element={<SupportAccess />} />
                      
                      {/* Enlace de invitación del perito (tanda 5, A11): público, fuera de
                          RoleBasedRouter; sin token válido no mete a nadie en ninguna empresa. */}
                      <Route path="/unirse/perito" element={<UnirsePerito />} />

                      {/* Auth routes */}
                      <Route path="/auth" element={<AppAuthPublicGuard><Auth /></AppAuthPublicGuard>} />
                      {/* Cambiar de app: llegada desde Ulture Tactic con un token de un solo uso (30-10-2026). */}
                      <Route path="/cambiar-app" element={<AppSwitch />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/app-auth" element={<AppAuth />} />
                      <Route path="/app-auth/free-signup" element={<AppAuthFreeSignup />} />
                      <Route path="/app-auth/complete-registration" element={<AppAuthCompleteRegistration />} />
                      <Route path="/complete-registration" element={<CompleteRegistration />} />
                      <Route path="/payment-success" element={<PaymentSuccessNative />} />
                      
                      <Route path="/register" element={<AppAuthPublicGuard><Register /></AppAuthPublicGuard>} />
                      <Route path="/auth-loading" element={<AuthLoading />} />
                      <Route path="/auth/error" element={<AuthErrorPage />} />
                      <Route path="/auth/apple/callback" element={<ApplePopupCallback />} />
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/onboarding/goals" element={<OnboardingGoals />} />
                      <Route path="/onboarding/setup" element={<OnboardingSetup />} />

                      {/* Terminal de la finca (30-10-2026): fuera del RoleBasedRouter, entra
                          gente de cualquier rol con su PIN y la pantalla no cambia por ello. */}
                      <Route path="/terminal" element={<TerminalPage />} />

                      {/* All protected routes handled by RoleBasedRouter */}
                      <Route path="/*" element={<RoleBasedRouter />} />
                    </Routes>
                    </ColdStartGate>
                  </ShopCartProvider>
                  </PageTitleProvider>
                  </SplashGate>
                </AuthProvider>
                </CookieConsentProvider>
              </BrowserRouter>
            </Suspense>
          </ErrorTrackerProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </I18nProvider>
  </ErrorBoundary>
);

export default App;

